import { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import type { ViewportBounds } from "../../state/discover-reducer";

interface Props {
  /** Below this zoom, emit null (list shows the full browse, not "in view"). */
  readonly minZoom: number;
  readonly onBounds: (bounds: ViewportBounds | null) => void;
  readonly debounceMs?: number;
}

/**
 * Publishes the map's visible bounds (debounced, on `idle`) so the list can
 * narrow to "places in view". Emits null below `minZoom`. Skips entirely when
 * its map is hidden: both the desktop and mobile Discover shells mount a map,
 * but only the visible one should drive the shared viewport state.
 */
export function BoundsReporter({ minZoom, onBounds, debounceMs = 200 }: Props) {
  const map = useMap();
  const cbRef = useRef(onBounds);
  useEffect(() => {
    cbRef.current = onBounds;
  }, [onBounds]);

  useEffect(() => {
    if (!map) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const emit = () => {
      const div = map.getDiv() as HTMLElement | null;
      // A display:none shell (the other breakpoint's map) reports 0×0 — ignore
      // it so the hidden map can't clobber the visible map's viewport.
      if (!div || div.offsetWidth === 0 || div.offsetHeight === 0) return;
      const zoom = map.getZoom() ?? 0;
      if (zoom < minZoom) {
        cbRef.current(null);
        return;
      }
      const b = map.getBounds();
      const c = map.getCenter();
      if (!b || !c) return;
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      cbRef.current({
        north: ne.lat(),
        east: ne.lng(),
        south: sw.lat(),
        west: sw.lng(),
        centerLat: c.lat(),
        centerLng: c.lng(),
      });
    };

    const listener = map.addListener("idle", () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(emit, debounceMs);
    });
    // Zooming out should restore the full browse immediately (no debounce) so
    // the list doesn't stutter through stale in-view items on a fast zoom-out.
    const zoomListener = map.addListener("zoom_changed", () => {
      if ((map.getZoom() ?? 0) < minZoom) cbRef.current(null);
    });
    return () => {
      if (timer) clearTimeout(timer);
      listener.remove();
      zoomListener.remove();
      // Clear the shared viewport when this map tears down (mode / breakpoint
      // switch) so a stale bounds can't keep the list narrowed with no map.
      cbRef.current(null);
    };
  }, [map, minZoom, debounceMs]);

  return null;
}
